import { DesignData, PageStepDirection } from '../types';

// The smallest gap between consecutive valid page counts, i.e. the number of
// pages one "add/remove pages" click must move to land on the next valid grid
// value. Returns 0 when there are fewer than two values (no meaningful step).
export function getGridStepInPages(validPageCounts: number[]): number {
  let step = 0;
  for (let i = 1; i < validPageCounts.length; i += 1) {
    const gap = validPageCounts[i] - validPageCounts[i - 1];
    if (gap > 0 && (step === 0 || gap < step)) {
      step = gap;
    }
  }
  return step;
}

// Sold page counts need not be evenly spaced (28 / 32 / 40), so a click moves to
// the next one in its direction: a fixed step would land between them, on a page
// count no variant sells. Undefined past either end.
export function getNeighbourPageCount(
  pageCount: number,
  validPageCounts: number[],
  direction: PageStepDirection,
): number | undefined {
  return direction === 'add'
    ? validPageCounts.find((count) => count > pageCount)
    : [...validPageCounts].reverse().find((count) => count < pageCount);
}

// How many spreads make up one "add/remove pages" step for this design.
//
// The step must move page_count to the next valid grid value, so the product
// can actually switch to the result. The grid comes from the ecommerce variant
// page counts when the product is sold online (`validPageCounts`, ascending),
// otherwise from the design's own `page_count_limit.increment`.
//
// Because a spread may hold one page (flat books: brochures) or two pages
// (spread books: photobooks), the number of spreads for one grid step is the
// step in pages divided by the pages per spread — e.g. a 4-page grid step is 4
// single-page spreads for a brochure but 2 two-page spreads for a photobook.
// Returns 0 when whole spreads cannot reach the next sold page count, and falls
// back to a single spread when no grid step is known.
function getSpreadCountForIncrement(
  designData: DesignData,
  spreadIndex: number,
  direction: PageStepDirection,
  validPageCounts?: number[],
): number {
  const pagesPerSpread = designData.spreads[spreadIndex]?.pages.length || 1;
  const soldPageCounts = validPageCounts && validPageCounts.length > 1 ? validPageCounts : undefined;

  if (soldPageCounts && designData.page_count !== undefined) {
    const neighbour = getNeighbourPageCount(designData.page_count, soldPageCounts, direction);
    if (neighbour !== undefined) {
      const stepInPages = Math.abs(neighbour - designData.page_count);
      // Rounding would land the book on a page count no variant sells.
      return stepInPages % pagesPerSpread ? 0 : stepInPages / pagesPerSpread;
    }
  }

  // Past either end the button is disabled; the smallest gap still gives its label a number.
  const stepInPages = soldPageCounts ? getGridStepInPages(soldPageCounts) : designData.page_count_limit?.increment;
  if (!stepInPages) {
    return 1;
  }
  if (soldPageCounts) {
    return Math.max(1, Math.round(stepInPages / pagesPerSpread));
  }
  // Whole spreads that land back on the increment grid: an odd increment on two-page spreads takes
  // two increments rather than ending between grid values.
  let pages = stepInPages;
  while (pages % pagesPerSpread) {
    pages += stepInPages;
  }
  return pages / pagesPerSpread;
}

export default getSpreadCountForIncrement;
